import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  ArrowLeft, Calendar, CheckCircle, ImagePlus, Mail, MessageSquare, OctagonX, Settings2, Users,
} from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { legacy } from '@/shared/api/client';
import { useCampaignScope } from '../model/campaign-scope';
import { PageHeader } from '@/shared/components/page-header';
import { Wizard, type WizardStep } from '@/shared/components/wizard';
import { Button } from '@/shared/ui/button';
import { Card } from '@/shared/ui/card';
import { Checkbox } from '@/shared/ui/checkbox';
import { DatePicker } from '@/shared/ui/date-picker';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Skeleton } from '@/shared/ui/skeleton';
import { Switch } from '@/shared/ui/switch';
import { Textarea } from '@/shared/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { RadioGroup, RadioGroupItem } from '@/shared/ui/radio-group';
import type { AutomatedCampaignPayload } from '@/shared/types/campaign';
import { CampaignAudiencePreview } from './campaign-audience-preview';
import { CampaignPhoneMockup } from './campaign-phone-mockup';
import { MessageFieldWithEmoji } from './message-field-with-emoji';
import { TimePicker } from './time-picker';
import {
  ATIVIDADE_OPTIONS, defaultAutomatedPayload, FAIXA_ETARIA_OPTIONS, FREQUENCIA_OPTIONS,
  GENERO_OPTIONS, GEO_PASSBOOK_IDS, PERFORMANCE_OPTIONS, WEEKDAY_OPTIONS,
} from '../model/campaign-form-constants';
import {
  campaignDispatchMeta,
  canStopAutomatedCampaign,
  formatCampaignCreatedAt,
  formatCampaignDate,
} from '../model/campaign-dates';
import { legacyCampaignToPayload } from '../model/campaign-mapper';
import {
  fetchCampaignClusters, fetchCampaignStores, fetchOwnerCampaignStores, fetchStates, getAutomatedCampaign,
  saveAutomatedCampaign, saveBirthdayCampaign, uploadCampaignEmailImage, type ClusterOption, type StoreOption,
} from '../api/use-campaigns';
import { fetchSegmentations, isSegmentationStale } from '@/features/segmentacoes';
import type { Segmentation } from '@/shared/types/segmentation';
import { Badge } from '@/shared/ui/badge';
import { channelLabel } from '@/features/marketing-shared/model/channels';

const PLATAFORMA = [
  { value: 'all', label: 'Todos' },
  { value: 'iOS', label: 'iOS' },
  { value: 'Android', label: 'Android' },
];
const MONTH_DAYS = Array.from({ length: 31 }, (_, i) => String(i + 1));

function parseStores(stores?: string | string[]) {
  if (!stores) return [] as string[];
  if (Array.isArray(stores)) return stores;
  return stores === 'all' ? ['all'] : stores.split(',').filter(Boolean);
}

function clusterValuesFromData(raw?: string) {
  if (!raw) return {} as Record<string, string | string[]>;
  try {
    const parsed = JSON.parse(raw) as unknown;
    const obj = Array.isArray(parsed) ? parsed[0] : parsed;
    if (!obj || typeof obj !== 'object') return {};
    const out: Record<string, string | string[]> = {};
    for (const [k, v] of Object.entries(obj as Record<string, string | string[]>)) {
      if (v) out[k.startsWith('cluster_') ? k : `cluster_${k}`] = v;
    }
    return out;
  } catch {
    return {};
  }
}

function buildClustersData(clusterValues: Record<string, string | string[]>) {
  const data: Record<string, string | string[]> = {};
  for (const [key, val] of Object.entries(clusterValues)) {
    if (val && (!Array.isArray(val) || val.length)) data[key] = val;
  }
  return Object.keys(data).length ? JSON.stringify(data) : '[]';
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-2">
      <Label className="block">{label}</Label>
      {children}
    </div>
  );
}

function OptSelect({
  label, value, onChange, options, placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: readonly { value: string; label: string }[];
  placeholder?: string;
}) {
  return (
    <Field label={label}>
      <Select value={value || '__empty'} onValueChange={(v) => onChange(v === '__empty' ? '' : v)}>
        <SelectTrigger><SelectValue placeholder={placeholder} /></SelectTrigger>
        <SelectContent>
          {options.map((o) => (
            <SelectItem key={o.value || '__empty'} value={o.value || '__empty'}>{o.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </Field>
  );
}

function SummaryRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex gap-3 border-b border-border/60 py-2.5 last:border-0">
      <dt className="w-36 shrink-0 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </dt>
      <dd className="min-w-0 flex-1 text-sm">{value || '—'}</dd>
    </div>
  );
}

export function AutomatedCampaignFormPage() {
  const { idPassbook, listPath, isOwnerScope, passbook, organizationName } = useCampaignScope();
  const { campaignId } = useParams<{ campaignId?: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEdit = Boolean(campaignId);

  const [form, setForm] = useState<AutomatedCampaignPayload>(() => {
    const initial = defaultAutomatedPayload();
    return initial;
  });
  const [clusterValues, setClusterValues] = useState<Record<string, string | string[]>>({});
  const [emailHtml, setEmailHtml] = useState('');
  const [emailBtnText, setEmailBtnText] = useState('');
  const [emailBtnLink, setEmailBtnLink] = useState('');
  const [stores, setStores] = useState<StoreOption[]>([]);
  const [clusters, setClusters] = useState<ClusterOption[]>([]);
  const [segmentations, setSegmentations] = useState<Segmentation[]>([]);
  const [states, setStates] = useState<Array<{ acronym: string; name?: string }>>([]);
  const [cities, setCities] = useState<string[]>([]);
  const [loading, setLoading] = useState(isEdit);
  const [busy, setBusy] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [createdAt, setCreatedAt] = useState<string | undefined>();
  const imageRef = useRef<HTMLInputElement>(null);

  const selectedStores = useMemo(() => parseStores(form.stores), [form.stores]);
  const showStores = !isOwnerScope && Boolean(passbook?.hasMultiStore);
  const showGeo = !isOwnerScope && GEO_PASSBOOK_IDS.has(idPassbook);
  const isEmail = form.tipo_disparo === 'email';
  const patch = (p: Partial<AutomatedCampaignPayload>) => setForm((f) => ({ ...f, ...p }));

  const showStop = isEdit && canStopAutomatedCampaign({
    active: form.ativo,
    frequencia: form.frequencia,
    expirar: form.expirar,
    frequencia_unico: form.frequencia_unico,
    frequencia_horas: form.frequencia_horas,
  });

  useEffect(() => {
    if (isOwnerScope) {
      void fetchOwnerCampaignStores(idPassbook).then(setStores);
      if (!isEdit) {
        setForm((f) => ({
          ...f,
          tipo_campanha: 'CO',
          stores: f.stores || 'all',
        }));
      }
      return;
    }
    if (passbook?.hasMultiStore) void fetchCampaignStores(idPassbook).then(setStores);
    void fetchCampaignClusters(idPassbook).then(setClusters);
    // .catch obrigatório: se a API ainda não tiver a rota de segmentações
    // (front no ar antes do backend), sem ele isto vira unhandled rejection.
    // A tela de campanha precisa continuar funcionando sem segmentação nenhuma.
    void fetchSegmentations(idPassbook).then(setSegmentations).catch(() => setSegmentations([]));
    if (showGeo) void fetchStates().then(setStates);
  }, [idPassbook, showGeo, isOwnerScope, isEdit, passbook?.hasMultiStore]);

  useEffect(() => {
    if (!campaignId) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const c = await getAutomatedCampaign(idPassbook, campaignId);
        if (cancelled) return;
        const payload = legacyCampaignToPayload(c, campaignId);
        setForm(payload);
        setCreatedAt(c.created_at);
        setClusterValues(clusterValuesFromData(payload.clusters_data));
        if (payload.tipo_disparo === 'email' && payload.description) setEmailHtml(payload.description);
      } catch {
        toast.error('Falha ao carregar campanha.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [idPassbook, campaignId]);

  useEffect(() => {
    if (!isEdit || loading || form.tipo_campanha === 'A') return;
    toast.error('Campanha automatizada não pode ser editada. Pause ou exclua na lista.');
    navigate(listPath, { replace: true });
  }, [isEdit, loading, form.tipo_campanha, navigate, listPath]);

  useEffect(() => {
    if (!showGeo || !form.state || form.state === '0') {
      setCities([]);
      return;
    }
    void legacy
      .get<Array<{ cities: string[] }>>(`/state/carregar-estados/${form.state}`)
      .then((r) => setCities(r[0]?.cities ?? []))
      .catch(() => setCities([]));
  }, [form.state, showGeo]);

  const toggleStore = (code: string, on: boolean) => {
    if (code === 'all') {
      patch({ stores: on ? 'all' : '' });
      return;
    }
    const next = on
      ? [...selectedStores.filter((s) => s !== 'all'), code]
      : selectedStores.filter((s) => s !== code);
    patch({ stores: next });
  };

  const toggleCluster = (key: string, value: string, on: boolean) => {
    setClusterValues((prev) => {
      const arr = Array.isArray(prev[key])
        ? (prev[key] as string[])
        : prev[key]
          ? [prev[key] as string]
          : [];
      return { ...prev, [key]: on ? [...arr, value] : arr.filter((v) => v !== value) };
    });
  };

  const validateSchedule = (): boolean => {
    if (!form.frequencia_horas?.trim()) {
      toast.error('Informe o horário do disparo.');
      return false;
    }
    if (form.frequencia === '7' && !form.frequencia_dia_semana) {
      toast.error('Selecione um dia da semana.');
      return false;
    }
    if (form.frequencia === '30' && !form.frequencia_dia?.trim()) {
      toast.error('Informe o dia do mês.');
      return false;
    }
    if (form.frequencia === '31' && !form.frequencia_unico?.trim()) {
      toast.error('Informe a data do disparo.');
      return false;
    }
    if (form.expirar && (!form.dt_inicio?.trim() || !form.dt_fim?.trim())) {
      toast.error('Informe as datas de início e fim da expiração.');
      return false;
    }
    return true;
  };

  const saveBirthday = async () => {
    if (!campaignId) return;
    if (!form.frequencia_horas?.trim()) {
      toast.error('Informe o horário do disparo.');
      return;
    }
    const description = form.description.trim();
    if (!description) {
      toast.error('Informe a mensagem da campanha.');
      return;
    }
    setBusy(true);
    try {
      await saveBirthdayCampaign(idPassbook, campaignId, {
        mensagem: description,
        frequencia_horas: form.frequencia_horas.trim().padStart(5, '0'),
      });
      toast.success('Campanha de aniversário atualizada.');
      await queryClient.invalidateQueries({ queryKey: ['campaigns', 'automated', idPassbook] });
      navigate(listPath);
    } catch {
      toast.error('Falha ao salvar campanha.');
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    if (!form.nome.trim()) {
      toast.error('Informe o nome da campanha.');
      return;
    }
    if (!validateSchedule()) return;
    const description = isEmail ? emailHtml.trim() : form.description.trim();
    if (!description) {
      toast.error('Informe a mensagem da campanha.');
      return;
    }
    setBusy(true);
    try {
      await saveAutomatedCampaign(idPassbook, {
        ...form,
        action: isEdit ? 'edit' : 'insert',
        id: campaignId,
        tipo_campanha: isOwnerScope ? 'CO' : form.tipo_campanha,
        description,
        clusters_data: buildClustersData(clusterValues),
        mais_informacao: form.tipo_disparo === 'whatsapp' ? form.mais_informacao : '',
      });
      toast.success(isEdit ? 'Campanha atualizada.' : 'Campanha criada.');
      navigate(listPath);
    } catch {
      toast.error('Falha ao salvar campanha.');
    } finally {
      setBusy(false);
    }
  };

  const stopCampaign = async () => {
    if (!campaignId || !showStop) return;
    setStopping(true);
    try {
      await saveAutomatedCampaign(idPassbook, {
        ...form,
        action: 'edit',
        id: campaignId,
        tipo_campanha: isOwnerScope ? 'CO' : form.tipo_campanha,
        ativo: false,
        description: isEmail ? emailHtml : form.description,
        clusters_data: buildClustersData(clusterValues),
      });
      patch({ ativo: false });
      toast.success('Campanha parada.');
      navigate(listPath);
    } catch {
      toast.error('Falha ao parar campanha.');
    } finally {
      setStopping(false);
    }
  };

  const freqOptions = FREQUENCIA_OPTIONS.filter((o) => form.tipo_campanha !== 'A' || o.value === '1');
  const freqLabel = freqOptions.find((f) => f.value === form.frequencia)?.label ?? form.frequencia;
  const disparoLabel = channelLabel(form.tipo_disparo);
  const dispatchMeta = campaignDispatchMeta(form);
  const createdMeta = formatCampaignCreatedAt(createdAt);
  const messagePreview = (isEmail ? emailHtml : form.description).trim();

  const selectedSegmentation = segmentations.find((s) => s._id === form.segmentationId);

  const previewAside =
    form.tipo_campanha === 'A' ? null : selectedSegmentation ? (
      <div className="rounded-xl border border-success/30 bg-success/5 px-4 py-3">
        <p className="mb-1.5 text-sm font-medium text-success">Leads na segmentação</p>
        <p className="text-2xl font-semibold tabular-nums text-primary">
          {(selectedSegmentation.leadCount ?? 0).toLocaleString('pt-BR')}
        </p>
        {isSegmentationStale(selectedSegmentation) ? (
          <Badge variant="warning" className="mt-2">
            Público desatualizado — abra a segmentação e clique em Atualizar
          </Badge>
        ) : null}
      </div>
    ) : (
      <CampaignAudiencePreview
        idPassbook={idPassbook}
        form={form}
        clusterValues={clusterValues}
        owner={isOwnerScope}
      />
    );

  const audienceFields = (
    <div className="space-y-4">
      {segmentations.length > 0 ? (
        <Field label="Audiência salva">
          <Select
            value={form.segmentationId || '__none'}
            onValueChange={(v) => patch({ segmentationId: v === '__none' ? undefined : v })}
          >
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none">Nenhuma — usar filtros clássicos abaixo</SelectItem>
              {segmentations.map((s) => (
                <SelectItem key={s._id} value={s._id}>
                  {s.nome} ({(s.leadCount ?? 0).toLocaleString('pt-BR')} leads)
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
      ) : null}
      {selectedSegmentation ? null : (isOwnerScope || showStores) && stores.length > 0 ? (
        <Field label={isOwnerScope ? 'Unidades' : 'Lojas'}>
          <div className="max-h-40 space-y-2 overflow-y-auto rounded-xl border border-border p-3">
            <label className="flex items-center gap-2 py-1.5 text-sm">
              <Checkbox
                checked={selectedStores.includes('all')}
                onCheckedChange={(v) => toggleStore('all', v === true)}
              />
              Todas as lojas
            </label>
            {stores.map((s) => {
              const code = String(s.idPassbook ?? s.storeCod ?? s._id ?? '');
              return (
                <label key={code} className="flex items-center gap-2 py-1.5 text-sm">
                  <Checkbox
                    checked={selectedStores.includes(code)}
                    onCheckedChange={(v) => toggleStore(code, v === true)}
                  />
                  {s.storeName ?? s.name ?? code}
                </label>
              );
            })}
          </div>
        </Field>
      ) : null}
      {selectedSegmentation ? null : (
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Plataforma">
          <Select value={form.plataforma || 'all'} onValueChange={(v) => patch({ plataforma: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {PLATAFORMA.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <OptSelect
          label="Inatividade"
          value={form.atividade ?? ''}
          onChange={(v) => patch({ atividade: v })}
          options={ATIVIDADE_OPTIONS}
        />
        <OptSelect
          label="Faixa etária"
          value={form.faixa_etaria ?? ''}
          onChange={(v) => patch({ faixa_etaria: v })}
          options={FAIXA_ETARIA_OPTIONS}
        />
        <OptSelect
          label="Gênero"
          value={form.genero ?? ''}
          onChange={(v) => patch({ genero: v })}
          options={GENERO_OPTIONS}
        />
        {showGeo ? (
          <>
            <Field label="Estado">
              <Select value={form.state || '0'} onValueChange={(v) => patch({ state: v, city: '0' })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">Selecione</SelectItem>
                  {states.map((s) => (
                    <SelectItem key={s.acronym} value={s.acronym}>{s.name ?? s.acronym}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Cidade">
              <Select value={form.city || '0'} onValueChange={(v) => patch({ city: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">Selecione</SelectItem>
                  {cities.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </>
        ) : null}
      </div>
      )}
      {selectedSegmentation ? null : clusters.length > 0 ? (
        <div className="grid gap-4 sm:grid-cols-2">
          {clusters.map((c) => {
            const key = `cluster_${c.nome}`;
            const arr = Array.isArray(clusterValues[key])
              ? (clusterValues[key] as string[])
              : clusterValues[key]
                ? [clusterValues[key] as string]
                : [];
            return (
              <Field key={key} label={c.nome.charAt(0).toUpperCase() + c.nome.slice(1)}>
                <div className="max-h-32 space-y-1.5 overflow-y-auto rounded-xl border border-border p-2">
                  {c.values.map((v) => (
                    <label key={v} className="flex items-center gap-2 py-1.5 text-sm">
                      <Checkbox
                        checked={arr.includes(v)}
                        onCheckedChange={(x) => toggleCluster(key, v, x === true)}
                      />
                      {v}
                    </label>
                  ))}
                </div>
              </Field>
            );
          })}
        </div>
      ) : null}
    </div>
  );

  const allSteps: WizardStep[] = [
    {
      id: 'dados',
      title: 'Dados',
      icon: Settings2,
      validate: () => {
        if (!form.nome.trim()) return toast.error('Informe o nome da campanha.'), false;
        if (!form.tipo_disparo) return toast.error('Selecione o método de disparo.'), false;
        return true;
      },
      content: (
        <div className="space-y-4">
          <div className="flex items-center justify-between rounded-xl border border-border p-3">
            <div>
              <p className="text-sm font-medium">Ativo</p>
              <p className="text-xs text-muted-foreground">Participa dos disparos agendados.</p>
            </div>
            <Switch checked={form.ativo} onCheckedChange={(v) => patch({ ativo: v })} />
          </div>
          <Field label="Nome">
            <Input
              value={form.nome}
              onChange={(e) => patch({ nome: e.target.value })}
              placeholder="Ex.: Novidades"
              readOnly={isEdit && form.tipo_campanha === 'A'}
            />
          </Field>
        </div>
      ),
    },
    {
      id: 'agendamento',
      title: 'Agendamento',
      icon: Calendar,
      validate: validateSchedule,
      content: (
        <div className="space-y-4">
          <Field label="Frequência">
            <Select
              value={form.frequencia}
              onValueChange={(v) => patch({
                frequencia: v,
                frequencia_dia: v === '30' ? form.frequencia_dia : '',
                frequencia_unico: v === '31' ? form.frequencia_unico : '',
                frequencia_dia_semana: v === '7' ? form.frequencia_dia_semana : '',
              })}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {freqOptions.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          {form.frequencia === '30' ? (
            <Field label="Dia do mês">
              <Select
                value={form.frequencia_dia || undefined}
                onValueChange={(v) => patch({ frequencia_dia: v })}
              >
                <SelectTrigger className="w-full sm:w-40"><SelectValue placeholder="Dia" /></SelectTrigger>
                <SelectContent className="max-h-60">
                  {MONTH_DAYS.map((d) => (
                    <SelectItem key={d} value={d}>{d}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          ) : null}

          {form.frequencia === '31' ? (
            <Field label="Data do disparo">
              <DatePicker
                value={form.frequencia_unico}
                onChange={(v) => patch({ frequencia_unico: v })}
                placeholder="Escolha a data"
                className="w-full sm:max-w-xs"
              />
            </Field>
          ) : null}

          {form.frequencia === '7' ? (
            <Field label="Dia da semana">
              <RadioGroup
                value={form.frequencia_dia_semana || undefined}
                onValueChange={(v) => patch({ frequencia_dia_semana: v })}
                className="flex flex-wrap gap-x-4 gap-y-2"
              >
                {WEEKDAY_OPTIONS.map((d) => (
                  <div key={d.value} className="flex items-center gap-2">
                    <RadioGroupItem value={d.value} id={`dow-${d.value}`} />
                    <Label htmlFor={`dow-${d.value}`} className="cursor-pointer font-normal">
                      {d.label}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </Field>
          ) : null}

          <TimePicker
            value={form.frequencia_horas}
            onChange={(v) => patch({ frequencia_horas: v })}
            label="Horário"
          />

          {form.tipo_campanha !== 'A' && form.frequencia !== '31' ? (
            <div className="space-y-3 rounded-xl border border-border p-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium">Expirar campanha</p>
                <Switch checked={form.expirar} onCheckedChange={(v) => patch({ expirar: v })} />
              </div>
              {form.expirar ? (
                <div className="grid gap-4 sm:grid-cols-2 sm:gap-6">
                  <Field label="Data início">
                    <DatePicker
                      value={form.dt_inicio}
                      onChange={(v) => patch({ dt_inicio: v })}
                      placeholder="Início"
                      className="w-full"
                    />
                  </Field>
                  <Field label="Data fim">
                    <DatePicker
                      value={form.dt_fim}
                      onChange={(v) => patch({ dt_fim: v })}
                      placeholder="Fim"
                      className="w-full"
                      fromDate={form.dt_inicio ? new Date(`${form.dt_inicio}T00:00:00`) : undefined}
                    />
                  </Field>
                </div>
              ) : null}
            </div>
          ) : null}

          {form.expirar || form.frequencia === '31' ? (
            <Field label="Performance">
              <Select
                value={String(form.performance ?? 3)}
                onValueChange={(v) => patch({ performance: Number(v) })}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PERFORMANCE_OPTIONS.map((d) => (
                    <SelectItem key={d} value={String(d)}>{d} dias</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          ) : null}
        </div>
      ),
    },
    {
      id: 'audiencia',
      title: 'Audiência',
      icon: Users,
      content: audienceFields,
    },
    {
      id: 'conteudo',
      title: 'Conteúdo',
      icon: MessageSquare,
      validate: () =>
        !isEmail && !form.description.trim()
          ? (toast.error('Informe a mensagem.'), false)
          : true,
      content: (
        <div className="space-y-4">
          {!isEmail ? (
            <MessageFieldWithEmoji
              label="Mensagem"
              value={form.description}
              onChange={(v) => patch({ description: v })}
              placeholder="Digite a mensagem da campanha…"
            />
          ) : (
            <p className="text-sm text-muted-foreground">Configure o e-mail na próxima etapa.</p>
          )}
        </div>
      ),
    },
    {
      id: 'email',
      title: 'E-mail marketing',
      icon: Mail,
      validate: () => (!emailHtml.trim() ? (toast.error('Monte o conteúdo do e-mail.'), false) : true),
      content: (
        <div className="space-y-4">
          <input
            ref={imageRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={async (e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              try {
                const { url } = await uploadCampaignEmailImage(file);
                setEmailHtml((h) => `${h}\n<img src="${url}" alt="" />`.trim());
                toast.success('Imagem adicionada.');
              } catch {
                toast.error('Falha no upload.');
              }
            }}
          />
          <Button type="button" variant="secondary" onClick={() => imageRef.current?.click()}>
            <ImagePlus className="size-4" /> Enviar imagem
          </Button>
          <Field label="HTML / mensagem">
            <Textarea rows={8} value={emailHtml} onChange={(e) => setEmailHtml(e.target.value)} />
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Texto do botão">
              <Input value={emailBtnText} onChange={(e) => setEmailBtnText(e.target.value)} />
            </Field>
            <Field label="Link do botão">
              <Input value={emailBtnLink} onChange={(e) => setEmailBtnLink(e.target.value)} />
            </Field>
          </div>
        </div>
      ),
    },
    {
      id: 'revisao',
      title: 'Revisão',
      icon: CheckCircle,
      content: (
        <div className="grid items-start gap-8 lg:grid-cols-[minmax(0,1fr)_280px]">
          <dl className="min-w-0 rounded-xl border border-border bg-muted/20 px-4">
            <SummaryRow label="Nome" value={form.nome} />
            <SummaryRow label="Disparo" value={disparoLabel} />
            <SummaryRow label="Frequência" value={freqLabel} />
            <SummaryRow
              label="Criação"
              value={
                isEdit && createdMeta.date
                  ? `${createdMeta.date}${createdMeta.time ? ` · ${createdMeta.time}` : ''}`
                  : 'Ao salvar'
              }
            />
            <SummaryRow
              label="Disparo em"
              value={
                [
                  dispatchMeta.dateLabel || formatCampaignDate(form.frequencia_unico),
                  dispatchMeta.timeLabel || form.frequencia_horas,
                ]
                  .filter(Boolean)
                  .join(' · ') || '—'
              }
            />
            {form.expirar ? (
              <SummaryRow
                label="Expira"
                value={`${formatCampaignDate(form.dt_inicio)} → ${formatCampaignDate(form.dt_fim)}`}
              />
            ) : null}
            <SummaryRow
              label="Mensagem"
              value={
                <span className="whitespace-pre-wrap break-words">
                  {messagePreview.slice(0, 280) || '—'}
                  {messagePreview.length > 280 ? '…' : ''}
                </span>
              }
            />
          </dl>
          {!isEmail && form.tipo_campanha !== 'A' ? (
            <div className="flex justify-center lg:justify-end">
              <CampaignPhoneMockup
                title={form.nome || organizationName || 'FixeCRM'}
                message={messagePreview}
                channel={form.tipo_disparo}
              />
            </div>
          ) : null}
        </div>
      ),
    },
  ];

  const steps = allSteps.filter((s) => {
    if (s.id === 'audiencia' && form.tipo_campanha === 'A') return false;
    if (s.id === 'email' && !isEmail) return false;
    return true;
  });

  if (loading || (isEdit && form.tipo_campanha !== 'A')) {
    return <Skeleton className="h-96 rounded-2xl" />;
  }

  if (isEdit && form.tipo_campanha === 'A') {
    return (
      <>
        <PageHeader
          title="Campanha de aniversário"
          hideMetaOnMobile
          description={organizationName}
          actions={
            <Button variant="secondary" onClick={() => navigate(listPath)}>
              <ArrowLeft className="size-4" /> Voltar
            </Button>
          }
        />
        <Card className="w-full max-w-2xl space-y-6 p-6 sm:p-8">
          <div>
            <p className="text-sm font-medium">{form.nome || 'Aniversário'}</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Dispara todo dia, para quem faz aniversário. Dá para mudar o horário e a mensagem.
            </p>
          </div>
          <TimePicker
            value={form.frequencia_horas}
            onChange={(v) => patch({ frequencia_horas: v })}
            label="Horário"
          />
          <MessageFieldWithEmoji
            label="Mensagem"
            value={form.description}
            onChange={(v) => patch({ description: v })}
            placeholder="Digite a mensagem da campanha…"
          />
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => navigate(listPath)} disabled={busy}>
              Cancelar
            </Button>
            <Button onClick={() => void saveBirthday()} disabled={busy}>
              {busy ? 'Salvando…' : 'Salvar'}
            </Button>
          </div>
        </Card>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={isEdit ? 'Editar campanha' : 'Nova campanha'}
        hideMetaOnMobile
        description={organizationName}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {showStop ? (
              <Button
                variant="destructive"
                disabled={stopping || busy}
                onClick={() => void stopCampaign()}
              >
                <OctagonX className="size-4" />
                {stopping ? (
                  'Parando…'
                ) : (
                  <>
                    Parar<span className="hidden sm:inline"> campanha</span>
                  </>
                )}
              </Button>
            ) : null}
            <Button variant="secondary" onClick={() => navigate(listPath)}>
              <ArrowLeft className="size-4" /> Voltar
            </Button>
          </div>
        }
      />
      <div className="grid w-full gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(280px,360px)]">
        <Card className="min-w-0 w-full p-6 sm:p-8">
          <Wizard
            steps={steps}
            compactStepper
            clickableStepper
            mobileOptimized
            onFinish={save}
            onCancel={() => navigate(listPath)}
            isSubmitting={busy}
            finishLabel={isEdit ? 'Salvar' : 'Criar campanha'}
          />
        </Card>
        {previewAside ? (
          <aside className="w-full min-w-0 lg:sticky lg:top-24 lg:self-start">{previewAside}</aside>
        ) : null}
      </div>
    </>
  );
}
